/**
 * Archive export — turn the ET's ordered picks (from buildPicks(): pen trims, ± adjustments,
 * pick order already applied) into segments the backend can cut, each from ITS OWN video.
 *
 * Contract with the backend (`make_video` → SQS → worker):
 *   archive_segments = [{ transcript_id, pk, start_in, end_in, text }, …]   (JSON, in play order)
 * `pk` is the Sentence pk when known (split pieces "456-a" and supercut pieces "456-sc0" map back
 * to 456) or null; the worker cuts by the explicit times either way.
 */
import { parseSheetId } from "./multiSheet";

/** Export options the archive can't do yet. Super Cut and GIF ride the same explicit-segment path
 * (the worker concatenates, then converts to GIF for that kind). */
export const ARCHIVE_UNSUPPORTED_EXPORTS = new Set([
  "Export XML for Premiere Pro",     // a multi-source EDL is its own feature
  "Create zip file with all SRT's",  // per-transcript bundle, not a cut
]);
export const ARCHIVE_UNSUPPORTED_NOTE = "not for archives yet";

export function pickToArchiveSegment(pick) {
  if (!pick) return null;
  const parsed = parseSheetId(pick.id);
  if (!parsed) return null;
  const pkStr = String(parsed.rawId).split("-")[0];
  const pk = /^\d+$/.test(pkStr) ? Number(pkStr) : null;
  const start = Number(pick.start);
  const end = Number(pick.end);
  if (!isFinite(start) || !isFinite(end) || end <= start) return null;
  return {
    transcript_id: Number(parsed.transcriptId),
    pk,
    start_in: start,
    end_in: end,
    text: pick.sentence || "",
    // Which video this line came from — the stitched SRT/VTT prefixes each cue with it, so a
    // subtitle file from an archive tells you the source instead of silently mixing them.
    source_label: pick.srcLabel || "",
  };
}

export function buildArchiveSegments(picks) {
  return (picks || []).map(pickToArchiveSegment).filter(Boolean);
}

/** "Archive: Debate 1 + Debate 2" — the default export/library title for a combined sheet. */
export function archiveExportTitle(sources, max = 80) {
  const names = (sources || []).map((s) => (s && s.label) || "").filter(Boolean);
  if (!names.length) return "Archive";
  let title = `Archive: ${names.join(" + ")}`;
  if (title.length > max) title = `${title.slice(0, max - 1).trimEnd()}…`;
  return title;
}

/** Which transcripts a set of segments touches (for the owner check / a library note). */
export const segmentTranscriptIds = (segments) =>
  [...new Set((segments || []).map((s) => s.transcript_id))];
